import { styled } from "@mui/material";
import SearchMatch from "_shared/_views/tables/search-match";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useLayoutEffect, useRef, useState } from "react";

/** The space between two tag chips, and between the last one shown and "+N". */
const TAG_GAP_PX = 4;

const Root = styled("div")(() => ({
  display: "flex",
  flex: "1 1 auto",
  flexDirection: "column",
  minWidth: 0,
}));
/**
 * The name the reader chooses by. It keeps a line of its own and may take a second before it gives
 * way, and the whole name is in its `title` when even two lines are not enough (#6609).
 */
const Name = styled(Typography)(() => ({
  display: "-webkit-box",
  fontSize: "1em",
  fontWeight: 500,
  minWidth: 0,
  overflow: "hidden",
  overflowWrap: "anywhere",
  // A table cell around it may say `nowrap`, which would keep the name to one clipped line.
  whiteSpace: "normal",
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: 2,
}));
const TagLine = styled("div")(() => ({
  display: "flex",
  flexWrap: "nowrap",
  gap: TAG_GAP_PX,
  minWidth: 0,
  overflow: "hidden",
  position: "relative",
}));
/** Every chip the line could draw, laid out off-screen so each one's width can be read. */
const TagMeasure = styled("div")(() => ({
  display: "flex",
  flexWrap: "nowrap",
  gap: TAG_GAP_PX,
  left: 0,
  pointerEvents: "none",
  position: "absolute",
  top: 0,
  visibility: "hidden",
}));
/**
 * A measured chip's text, drawn by CSS from `data-label` so the same words are not in the page twice
 * for a search or a screen reader to find.
 */
const MeasuredLabel = styled("span")(() => ({
  "&::before": { content: "attr(data-label)" },
}));
const TagChip = styled(Chip)(({ theme }) => ({
  color: theme.palette.text.secondary,
  flex: "0 0 auto",
  maxWidth: "100%",
}));

type TemplateTagLineProps = {
  tags: readonly string[];
  className?: string;
};

/**
 * A template's tags on one line, as many as fit, then "+N" for the rest. Hovering or focusing "+N"
 * lists what it stands for, so no tag is out of reach because the line is short.
 */
export function TemplateTagLine(props: Readonly<TemplateTagLineProps>) {
  const { tags, className } = props;
  const lineRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(tags.length);
  // The tags as one value, so a caller that maps them afresh each render does not measure again.
  const tagsKey = tags.join("\u0000");

  useLayoutEffect(() => {
    const line = lineRef.current;
    const measure = measureRef.current;
    if (!line || !measure) return;
    const fit = () => {
      const chips = Array.from(measure.children) as HTMLElement[];
      const overflowChip = chips.pop();
      setVisibleCount(
        countTagsThatFit({
          availableWidth: line.clientWidth,
          tagWidths: chips.map((chip) => chip.offsetWidth),
          overflowWidth: overflowChip?.offsetWidth ?? 0,
          gap: TAG_GAP_PX,
        }),
      );
    };
    fit();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(fit);
    observer.observe(line);
    return () => observer.disconnect();
  }, [tagsKey]);

  if (tags.length === 0) return null;

  const shownTags = tags.slice(0, visibleCount);
  const hiddenTags = tags.slice(visibleCount);
  return (
    <TagLine ref={lineRef} className={className} data-testid="template-tag-line">
      {/* Marked where a search around the line reads tags: the template panels and the Fee Schedules page (#6590). */}
      {shownTags.map((tag) => (
        <TagChip key={tag} label={<SearchMatch text={tag} />} size="small" />
      ))}
      {hiddenTags.length > 0 && (
        <Tooltip title={hiddenTags.join(", ")}>
          <TagChip
            aria-label={`${hiddenTags.length} more ${hiddenTags.length === 1 ? "tag" : "tags"}: ${hiddenTags.join(", ")}`}
            label={`+${hiddenTags.length}`}
            size="small"
            tabIndex={0}
          />
        </Tooltip>
      )}
      <TagMeasure ref={measureRef} aria-hidden>
        {tags.map((tag) => (
          <MeasuredChip key={tag} label={tag} />
        ))}
        {/* The widest "+N" this line can need, so the space kept for it is never too small. */}
        <MeasuredChip label={`+${tags.length}`} />
      </TagMeasure>
    </TagLine>
  );
}

function MeasuredChip(props: Readonly<{ label: string }>) {
  return <TagChip data-label={props.label} label={<MeasuredLabel data-label={props.label} />} size="small" />;
}

type TemplateNameWithTagsProps = {
  name: string;
  tags: readonly string[];
  className?: string;
};

/**
 * A template's name, with its tags on the line beneath it. The name is what the reader picks a
 * template by, so it comes first and keeps its line; the tags take what is left of the line below
 * (#6609). Used wherever a fee schedule template is offered by name: the proposal tabs' template
 * panel and the Select Existing Template drawer.
 */
export default function TemplateNameWithTags(props: Readonly<TemplateNameWithTagsProps>) {
  const { name, tags, className } = props;
  return (
    <Root className={className}>
      <Name title={name}>
        <SearchMatch text={name} />
      </Name>
      <TemplateTagLine tags={tags} />
    </Root>
  );
}

/**
 * How many tags fit on a line `availableWidth` wide, keeping room for "+N" whenever any are left
 * over. A line that has not been laid out yet (width 0) shows every tag rather than none.
 */
export function countTagsThatFit(args: {
  availableWidth: number;
  tagWidths: readonly number[];
  overflowWidth: number;
  gap: number;
}): number {
  const { availableWidth, tagWidths, overflowWidth, gap } = args;
  if (availableWidth <= 0) return tagWidths.length;

  const totalWidth = tagWidths.reduce((sum, width) => sum + width, 0) + gap * Math.max(tagWidths.length - 1, 0);
  if (totalWidth <= availableWidth) return tagWidths.length;

  let usedWidth = overflowWidth;
  let count = 0;
  for (const width of tagWidths) {
    if (usedWidth + width + gap > availableWidth) break;
    usedWidth += width + gap;
    count++;
  }
  return count;
}
